'use client';
import { useMediaQuery } from './useMediaQuery';

export type BreakpointKey = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const BREAKPOINT_VARIABLES: Record<BreakpointKey, string> = {
  xs: '--breakpoint-xs',
  sm: '--breakpoint-sm',
  md: '--breakpoint-md',
  lg: '--breakpoint-lg',
  xl: '--breakpoint-xl',
};

function getBreakpointValue(key: BreakpointKey): string {
  if (typeof window === 'undefined') return '0px';
  const variableName = BREAKPOINT_VARIABLES[key];
  const value = window.getComputedStyle(document.documentElement).getPropertyValue(variableName);
  if (!value.trim()) {
    console.warn(`Breakpoint CSS variable ${variableName} not found.`);
    return '0px';
  }
  return value;
}

/** Pixel value of a `--breakpoint-*` token, for callers comparing against a measured width instead of using `matchMedia`. */
export function getBreakpointPixels(key: BreakpointKey): number {
  const match = /^(-?\d*\.?\d+)(px|rem|em)?$/.exec(getBreakpointValue(key).trim());
  if (!match) return 0;
  const value = Number(match[1]);
  if (match[2] !== 'rem' && match[2] !== 'em') return value;
  const rootFontSize = typeof document === 'undefined' ? 16 : Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  return value * rootFontSize;
}

/**
 * Returns whether the viewport currently matches one of Spellbook's design-system breakpoints.
 *
 * Reads the threshold from the corresponding `--breakpoint-*` CSS custom property (defined by
 * Spellbook's theme) rather than a hardcoded pixel value, so it can never drift out of sync with
 * Tailwind's own breakpoints. Builds on `useMediaQuery` — see that hook for a raw media-query
 * escape hatch (e.g. `prefers-color-scheme`) when you're not checking a breakpoint.
 *
 * @param key - Breakpoint identifier (`xs` | `sm` | `md` | `lg` | `xl`).
 * @param type - `'up'` (min-width, default) or `'down'` (max-width).
 * @returns `true` when the viewport currently satisfies the breakpoint condition.
 *
 * @example
 * ```tsx
 * const isMdUp = useBreakpoint('md');       // viewport width >= --breakpoint-md
 * const isSmDown = useBreakpoint('sm', 'down'); // viewport width <= --breakpoint-sm
 *
 * return isMdUp ? <DesktopNav /> : <MobileNav />;
 * ```
 */
export function useBreakpoint(key: BreakpointKey, type: 'up' | 'down' = 'up'): boolean {
  const value = getBreakpointValue(key);
  const query = type === 'up' ? `(min-width: ${value})` : `(max-width: ${value})`;
  return useMediaQuery(query);
}
